import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Modal, FormGroup, FormControlLabel,
  Checkbox, RadioGroup, Radio, Select, MenuItem, FormControl,
  Slide, Snackbar, Alert, FormHelperText,
} from '@mui/material';

const API_BASE = 'https://feescrita-138417386089.us-central1.run.app';
const DISMISSED_KEY = 'feescrita_push_dismissed';
const PREFS_KEY = 'feescrita_push_prefs';
export const READ_COUNT_KEY = 'feescrita_posts_read';

const INTERESTS = [
  { key: 'familia', label: '👨‍👩‍👧‍👦 Família' },
  { key: 'fe_esperanca', label: '🙏 Fé e Esperança' },
  { key: 'trabalho', label: '💼 Trabalho' },
  { key: 'relacionamentos', label: '❤️ Relacionamentos' },
  { key: 'superacao', label: '💪 Superação' },
];

const TIME_OPTIONS = ['06:00', '07:00', '08:00', '12:00', '18:00', '20:00'];

const PushNotificationBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefsModal, setShowPrefsModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [interests, setInterests] = useState(['familia', 'fe_esperanca']);
  const [time, setTime] = useState('07:00');
  const [frequency, setFrequency] = useState('daily');

  useEffect(() => {
    const prefs = localStorage.getItem(PREFS_KEY);
    if (prefs) return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed && new Date(dismissed) > new Date()) return;

    const readCount = parseInt(localStorage.getItem(READ_COUNT_KEY) || '0', 10);
    const delay = readCount >= 2 ? 5000 : 45000;

    const timer = setTimeout(() => setShowBanner(true), delay);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    localStorage.setItem(DISMISSED_KEY, expiry.toISOString());
  };

  const handleReceive = () => {
    setShowBanner(false);
    setShowPrefsModal(true);
  };

  const toggleInterest = (key) => {
    setInterests(prev => prev.includes(key) ? prev.filter(i => i !== key) : [...prev, key]);
  };

  const savePreferences = async (prefs) => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    try {
      await fetch(`${API_BASE}/api/push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: prefs }),
      });
    } catch {
      // Saved locally even if backend fails
    }
  };

  const handleConfirm = async () => {
    if (interests.length === 0) return;
    const prefs = { interests, time, frequency };
    await savePreferences(prefs);
    setShowPrefsModal(false);
    setShowSuccess(true);
  };

  const handleSkip = async () => {
    const prefs = { interests: ['familia', 'fe_esperanca'], time: '07:00', frequency: 'daily' };
    await savePreferences(prefs);
    setShowPrefsModal(false);
    setShowSuccess(true);
  };

  return (
    <>
      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            display: 'flex',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 2.5,
              mb: 0,
              borderRadius: '16px 16px 0 0',
              maxWidth: 480,
              width: '100%',
              borderTop: '4px solid #4A90D9',
            }}
            role="region"
            aria-label="Convite para receber devocionais"
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Typography sx={{ fontSize: '30px', lineHeight: 1, mt: 0.3 }}>🔔</Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Receba devocionais no seu celular!
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Enviamos reflexões diárias para fortalecer sua fé.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleReceive}
                    sx={{ backgroundColor: '#4A90D9', borderRadius: '20px', px: 2.5 }}
                  >
                    Receber devocionais
                  </Button>
                  <Button variant="text" onClick={handleDismiss} sx={{ color: '#aaa', fontSize: '13px' }}>
                    Agora não
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Slide>

      <Modal open={showPrefsModal} onClose={() => setShowPrefsModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 460 },
            maxHeight: '90vh',
            overflowY: 'auto',
            backgroundColor: 'white',
            borderRadius: '16px',
            p: 3,
            outline: 'none',
          }}
          role="dialog"
          aria-label="Preferências de notificação"
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            ⚙️ Personalize sua experiência
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2.5 }}>
            Vamos enviar conteúdo que faça sentido para você.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Que tipo de conteúdo te interessa?
          </Typography>
          <FormGroup sx={{ mb: 0.5 }}>
            {INTERESTS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={interests.includes(key)}
                    onChange={() => toggleInterest(key)}
                    sx={{ color: '#4A90D9', '&.Mui-checked': { color: '#4A90D9' } }}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
          {interests.length === 0 && (
            <FormHelperText error sx={{ mb: 1.5 }}>Selecione pelo menos um tema.</FormHelperText>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Melhor horário para você
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <Select value={time} onChange={e => setTime(e.target.value)}>
              {TIME_OPTIONS.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Com que frequência?
          </Typography>
          <RadioGroup value={frequency} onChange={e => setFrequency(e.target.value)} sx={{ mb: 3 }}>
            <FormControlLabel
              value="daily"
              control={<Radio sx={{ color: '#4A90D9', '&.Mui-checked': { color: '#4A90D9' } }} />}
              label="Diário (recomendado)"
            />
            <FormControlLabel
              value="three_weekly"
              control={<Radio sx={{ color: '#4A90D9', '&.Mui-checked': { color: '#4A90D9' } }} />}
              label="3x por semana"
            />
            <FormControlLabel
              value="weekly"
              control={<Radio sx={{ color: '#4A90D9', '&.Mui-checked': { color: '#4A90D9' } }} />}
              label="Semanal"
            />
          </RadioGroup>

          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            disabled={interests.length === 0}
            sx={{ backgroundColor: '#4A90D9', borderRadius: '20px', mb: 1.2 }}
          >
            Confirmar configurações
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleSkip}
            sx={{ color: '#aaa', fontSize: '13px' }}
          >
            Pular, usar padrão
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)} sx={{ width: '100%' }}>
          🎉 Pronto! Você receberá devocionais para fortalecer sua fé.
        </Alert>
      </Snackbar>
    </>
  );
};

export default PushNotificationBanner;
